/**
 * V36.71 — Registre Propositions ESS BATICO (Super Admin)
 *
 * Affiche la liste paginée des PCS avec :
 *   - Onglets de filtre par statut (En cours / Signé / Expiré / Tous)
 *   - Recherche full-text (devis_id, raison sociale, email)
 *   - Actions par ligne : Visualiser (modal PDF), Imprimer, Télécharger
 *   - Lien GED si le PDF signé est archivé
 *
 * Design "institutional premium" : gradient marine, motifs de fond, cartes
 * avec backdrop blur, animations subtiles.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  FileText, Search, Download, Eye, Printer, Loader2, ShieldCheck,
  Clock, AlertTriangle, ChevronLeft, ChevronRight, Archive, ExternalLink,
  Award, FileSpreadsheet, RefreshCw, Database,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { getToken } from '../services/tokenService';
import PropositionPreviewDialog from './PropositionPreviewDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUTS = [
  { key: 'tous', label: 'Tous', icon: FileText, color: 'slate' },
  { key: 'en_cours', label: 'En cours', icon: Clock, color: 'amber' },
  { key: 'signe', label: 'Signés', icon: ShieldCheck, color: 'emerald' },
  { key: 'expire', label: 'Expirés', icon: AlertTriangle, color: 'rose' },
];

const STATUT_BADGE = {
  en_cours: { label: 'En cours', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  signe: { label: 'Signé', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  expire: { label: 'Expiré', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
};

function fmtEUR(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return Number(v).toLocaleString('fr-FR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }) + ' €';
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return s; }
}

export default function PropositionsESSRegistre() {
  const [statut, setStatut] = useState('tous');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1, counts: {} });
  const [previewDevis, setPreviewDevis] = useState(null);
  const [_previewBlob, setPreviewBlob] = useState(null);
  const [previewLoading, _setPreviewLoading] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);

  const fetchRegistre = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        statut, page: String(page), limit: String(limit),
      });
      if (search.trim()) params.append('search', search.trim());
      const r = await fetch(`${API}/batico/proposition/admin/registre?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setData(j);
    } catch (e) {
      toast.error(`Chargement du registre échoué : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [statut, search, page, limit]);

  useEffect(() => { fetchRegistre(); }, [fetchRegistre]);

  // Reset page when filter changes
  useEffect(() => { setPage(1); }, [statut, search]);

  const _handlePreview = async (devis_id) => {
    setPreviewDevis(devis_id);
  };

  const handleOpenInline = (devis_id) => {
    // Ouvre le PDF dans un nouvel onglet avec auth via fetch+blob (l'URL directe
    // ne porte pas le Bearer ; on télécharge en mémoire puis on ouvre une blob URL).
    fetch(
      `${API}/batico/proposition/admin/registre/${encodeURIComponent(devis_id)}/pdf`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    )
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        if (!win) {
          const a = document.createElement('a');
          a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer';
          document.body.appendChild(a); a.click(); a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch(e => toast.error(`Ouverture échouée : ${e.message}`));
  };

  const handleDownload = async (devis_id) => {
    try {
      const r = await fetch(
        `${API}/batico/proposition/admin/registre/${encodeURIComponent(devis_id)}/pdf?download=1`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Proposition_ESS_${devis_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
      toast.success('PDF téléchargé');
    } catch (e) {
      toast.error(`Téléchargement échoué : ${e.message}`);
    }
  };

  const handleExport = async (fmt) => {
    try {
      const params = new URLSearchParams({ format: fmt });
      if (statut && statut !== 'tous') params.append('statut', statut);
      if (search.trim()) params.append('search', search.trim());
      const r = await fetch(
        `${API}/batico/proposition/admin/registre/export?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const ext = fmt === 'csv' ? 'csv' : 'xlsx';
      const a = document.createElement('a');
      a.href = url;
      a.download = `registre_pcs_${statut}_${ts}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(`Export ${fmt} échoué : ${e.message}`);
    }
  };

  const handleRegenerate = async (devis_id) => {
    if (!window.confirm(
      `Régénérer le PDF de la Proposition ESS ${devis_id} ?\n\n` +
      "• L'ancien PDF sera sauvegardé (suffixe _OLD_avant_regen_<timestamp>).\n" +
      "• Le calcul du Montant Global sera recalculé selon les règles actuelles.\n" +
      "• Fonctionne aussi pour les anciens devis (souscriptions / GED).\n\n" +
      "Confirmer ?"
    )) return;
    setRegeneratingId(devis_id);
    try {
      const r = await fetch(
        `${API}/batico/proposition/${encodeURIComponent(devis_id)}/regenerate-pdf`,
        { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      const corr = j.incoherence_corrigee
        ? ` • Incohérence corrigée (${fmtEUR(j.montant_ttc_avant)} → ${fmtEUR(j.montant_ttc_apres)})`
        : '';
      const src = j.source_collection ? ` • Source : ${j.source_collection}` : '';
      toast.success(`PDF régénéré pour ${devis_id}${corr}${src}`);
      fetchRegistre();
    } catch (e) {
      toast.error(`Régénération échouée : ${e.message}`);
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleRunBatch = async () => {
    if (!window.confirm("Lancer la génération batch des PDFs (Propositions ESS + Conventions) ?\n\nCela peut prendre quelques minutes selon le volume.")) {
      return;
    }
    try {
      toast.info("Batch PDF démarré en arrière-plan…");
      const r = await fetch(`${API}/batico/proposition/admin/batch/pdf-generation/run`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      if (j.ok === false) {
        toast.warning(j.error || "Batch déjà en cours");
      } else {
        toast.success(`Batch OK — ${j.last_propositions_processed || 0} PCS + ${j.last_conventions_processed || 0} conventions archivés`);
        fetchRegistre();
      }
    } catch (e) {
      toast.error(`Batch échoué : ${e.message}`);
    }
  };

  const handleBackfillActivites = async () => {
    // Étape 1 : Dry run obligatoire pour avoir un aperçu
    if (!window.confirm(
      "Re-backfill des activités déclarées sur les souscriptions ?\n\n" +
      "Étape 1/2 : Audit (dry-run, aucune écriture) — affiche le nombre " +
      "de souscriptions qui seraient mises à jour.\n\n" +
      "Lancer l'audit ?"
    )) return;

    try {
      toast.info("Audit des activités en cours…");
      const dryR = await fetch(`${API}/admin/maintenance/backfill-activites-souscriptions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dry_run: true, limit: 5000 }),
      });
      const dryJ = await dryR.json();
      if (!dryR.ok) throw new Error(dryJ.detail || `HTTP ${dryR.status}`);

      const wouldUpdate = dryJ.updated || 0;
      const noProp = dryJ.no_proposition_found || 0;
      const alreadyOk = dryJ.skipped_already_filled || 0;

      if (wouldUpdate === 0) {
        toast.success(
          `Audit : aucune souscription à mettre à jour ` +
          `(déjà OK : ${alreadyOk}, sans proposition source : ${noProp})`
        );
        return;
      }

      // Étape 2 : confirmation explicite
      const goAhead = window.confirm(
        `Audit terminé.\n\n` +
        `• Souscriptions à mettre à jour : ${wouldUpdate}\n` +
        `• Déjà OK (skip) : ${alreadyOk}\n` +
        `• Sans proposition source (skip) : ${noProp}\n\n` +
        `Étape 2/2 : Appliquer la mise à jour DÉFINITIVE en base ?`
      );
      if (!goAhead) {
        toast.info("Mise à jour annulée. Aucune modification.");
        return;
      }

      toast.info(`Application en cours sur ${wouldUpdate} souscriptions…`);
      const applyR = await fetch(`${API}/admin/maintenance/backfill-activites-souscriptions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dry_run: false, limit: 5000 }),
      });
      const applyJ = await applyR.json();
      if (!applyR.ok) throw new Error(applyJ.detail || `HTTP ${applyR.status}`);
      toast.success(
        `Backfill terminé — ${applyJ.updated} souscriptions mises à jour ` +
        `(skip OK: ${applyJ.skipped_already_filled}, sans prop: ${applyJ.no_proposition_found})`
      );
    } catch (e) {
      toast.error(`Backfill échoué : ${e.message}`);
    }
  };

  const counts = data.counts || {};

  return (
    <div className="space-y-6">
      {/* Header institutionnel */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white shadow-xl">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h60v60H0z'/%3E%3Cpath d='M30 30m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0' fill='rgba(255,255,255,0.3)'/%3E%3C/g%3E%3C/svg%3E\")",
        }} />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400/30 to-amber-600/20 backdrop-blur border border-amber-300/40">
                <Award className="h-6 w-6 text-amber-300" />
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight" data-testid="registre-pcs-title">
                  Registre des Propositions ESS
                </h2>
                <p className="text-sm text-slate-300">
                  PCS BATICO archivés et en cours — Traçabilité totale, accès Super Admin
                </p>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-black text-amber-300" data-testid="registre-pcs-count">
              {counts.tous ?? 0}
            </div>
            <div className="text-xs uppercase tracking-widest text-slate-300 mb-3">
              Propositions enregistrées
            </div>
            <div className="flex items-center gap-2 justify-end flex-wrap">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => handleExport('xlsx')}
                data-testid="registre-export-xlsx"
                className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
              >
                <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => handleExport('csv')}
                data-testid="registre-export-csv"
                className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
              >
                <Download className="h-4 w-4 mr-1.5" /> CSV
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleRunBatch}
                data-testid="registre-run-batch"
                className="bg-amber-500/30 hover:bg-amber-500/50 border-amber-300/50 text-amber-50"
                title="Lancer la génération batch des PDFs manquants"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" /> Batch PDF
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleBackfillActivites}
                data-testid="registre-backfill-activites"
                className="bg-emerald-500/30 hover:bg-emerald-500/50 border-emerald-300/50 text-emerald-50"
                title="Re-backfill des activités manquantes sur les anciennes souscriptions (Audit puis Apply)"
              >
                <Database className="h-4 w-4 mr-1.5" /> Backfill activités
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="rounded-xl border border-slate-200 bg-white/80 backdrop-blur p-4 shadow-sm">
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {STATUTS.map((s) => {
            const Icon = s.icon;
            const active = statut === s.key;
            const c = counts[s.key] ?? 0;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setStatut(s.key)}
                data-testid={`registre-filter-${s.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                  active
                    ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <Icon className={`h-4 w-4 ${active ? 'text-amber-300' : ''}`} />
                <span>{s.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  active ? 'bg-amber-400/30 text-amber-100' : 'bg-slate-100 text-slate-600'
                }`}>
                  {c}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Rechercher par devis_id, raison sociale, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="registre-search-input"
            className="pl-9 bg-white border-slate-200"
          />
        </div>
      </div>

      {/* Tableau */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : data.items.length === 0 ? (
          <div className="py-16 text-center" data-testid="registre-empty">
            <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucune proposition ne correspond à ces critères.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                <tr className="text-xs uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-3 text-left">Devis</th>
                  <th className="px-4 py-3 text-left">Membre</th>
                  <th className="px-4 py-3 text-right">Montant Global</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                  <th className="px-4 py-3 text-left">Émis le</th>
                  <th className="px-4 py-3 text-left">Échéance / Signé</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((it) => {
                  const badge = STATUT_BADGE[it.statut] || STATUT_BADGE.en_cours;
                  return (
                    <tr key={it.devis_id} className="hover:bg-slate-50 transition-colors" data-testid={`registre-row-${it.devis_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs font-semibold text-slate-900">{it.devis_id}</div>
                        {it.version > 1 && (
                          <div className="text-[10px] text-slate-400">v{it.version} • parent {it.parent_devis_id}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{it.membre_raison_sociale || '—'}</div>
                        <div className="text-xs text-slate-500">{it.membre_email || it.membre_siret || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-bold text-slate-900">{fmtEUR(it.montant_ttc)}</div>
                        <div className="text-xs text-slate-400">HT {fmtEUR(it.montant_ht)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`border ${badge.cls}`}>{badge.label}</Badge>
                        {it.ged_document_id && (
                          <div className="text-[10px] text-emerald-700 mt-1 inline-flex items-center gap-1">
                            <Archive className="h-3 w-3" /> Archivé GED
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(it.created_at)}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {it.statut === 'signe'
                          ? <span className="text-emerald-700 font-semibold">{fmtDate(it.signed_at)}</span>
                          : fmtDate(it.expires_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenInline(it.devis_id)}
                            data-testid={`registre-view-${it.devis_id}`}
                            title="Visualiser"
                            className="h-8 w-8 p-0 hover:bg-blue-50"
                          >
                            <Eye className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenInline(it.devis_id)}
                            data-testid={`registre-print-${it.devis_id}`}
                            title="Ouvrir / Imprimer"
                            className="h-8 w-8 p-0 hover:bg-slate-100"
                          >
                            <Printer className="h-4 w-4 text-slate-600" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownload(it.devis_id)}
                            data-testid={`registre-download-${it.devis_id}`}
                            title="Télécharger"
                            className="h-8 w-8 p-0 hover:bg-emerald-50"
                          >
                            <Download className="h-4 w-4 text-emerald-600" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={regeneratingId === it.devis_id}
                            onClick={() => handleRegenerate(it.devis_id)}
                            data-testid={`registre-regenerate-${it.devis_id}`}
                            title="Régénérer le PDF (anciens devis compatibles)"
                            className="h-8 w-8 p-0 hover:bg-amber-50"
                          >
                            {regeneratingId === it.devis_id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                            ) : (
                              <RefreshCw className="h-4 w-4 text-amber-600" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <div className="text-xs text-slate-500">
              Page {data.page} / {data.total_pages} • {data.total} résultat{data.total > 1 ? 's' : ''}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                data-testid="registre-page-prev"
              >
                <ChevronLeft className="h-4 w-4" /> Préc.
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={page >= data.total_pages}
                onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}
                data-testid="registre-page-next"
              >
                Suiv. <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal preview (métadonnées + lien GED si dispo) */}
      <PropositionPreviewDialog
        previewDevis={previewDevis}
        setPreviewDevis={setPreviewDevis}
        setPreviewBlob={setPreviewBlob}
        previewLoading={previewLoading}
        data={data}
        handleDownload={handleDownload}
      />
    </div>
  );
}
